import type { HelpContent } from './types';

export const settingsNotificationsHelp: HelpContent = {
  pageTitle: 'Notifications',
  description: 'The Notifications settings page is where administrators configure which system events trigger automated alerts and how those alerts are delivered.',

  functionality: {
    overview:
      'This page lists all notification types grouped by category. Each notification can be toggled on or off. Some notifications have additional configuration options such as a delivery schedule or a delay period.',
    keyFeatures: [
      {
        label: 'Project event notifications',
        detail: 'Toggle alerts for project assignment, status changes, health changes, and end-date proximity.',
      },
      {
        label: 'Budget and financial alerts',
        detail: 'Configure alerts that fire when cost or hours budgets exceed defined thresholds.',
      },
      {
        label: 'Billing trigger schedule',
        detail: 'Set which days of the week and what time the billing trigger notification is sent.',
      },
      {
        label: 'WIP threshold alerts',
        detail: 'Define the aged WIP value threshold that triggers a WIP warning notification.',
      },
      {
        label: 'Resourcing notifications',
        detail: 'Configure alerts for unfilled project roles, including an optional delay before the alert fires.',
      },
    ],
    sectionsAndFields: [
      {
        name: 'Project Assignment',
        description: 'Sends a notification when a user is added as a key person or resource on a project.',
      },
      {
        name: 'Project Over Budget',
        description: 'Fires when a project\'s cost exceeds its cost budget.',
      },
      {
        name: 'Hours Budget Critical',
        description: 'Fires when a project\'s actual hours exceed its hours budget.',
      },
      {
        name: 'Sales Under Budget',
        description: 'Fires when invoiced or recognised revenue falls below the sales budget.',
      },
      {
        name: 'Billing Trigger Date',
        description: 'A scheduled notification sent on configured days and at a configured time — used to prompt billing reviews.',
      },
      {
        name: 'Billing Trigger Days',
        description: 'Checkboxes for Mon–Sun that control which days the billing trigger notification is sent.',
      },
      {
        name: 'Billing Trigger Time (UTC)',
        description: 'The time of day (in UTC) at which the billing trigger notification is dispatched.',
      },
      {
        name: 'Fixed Price Billing %',
        description: 'Fires when a fixed-price project reaches a configurable billing percentage milestone.',
      },
      {
        name: 'Project Health Changed',
        description: 'Fires when a project\'s health status changes (e.g. from Green to Amber).',
      },
      {
        name: 'Project End Date Approaching',
        description: 'Fires when a project\'s end date is within a configured number of days.',
      },
      {
        name: 'Invoice Overdue',
        description: 'Fires when an invoice passes its due date without being paid.',
      },
      {
        name: 'Schedule Overrun Detected',
        description: 'Fires when actual progress falls behind the planned schedule.',
      },
      {
        name: 'Aged WIP Alert',
        description: 'Fires when unbilled WIP for a project exceeds the configured aged WIP threshold.',
      },
      {
        name: 'Project Status Changed',
        description: 'Fires when the status of a project is updated by any user.',
      },
      {
        name: 'Project Role Unfilled',
        description: 'Fires when a required project role has no user assigned after an optional delay period.',
      },
      {
        name: 'Role Unfilled Delay',
        description: 'Number of hours after project creation before the unfilled role notification is triggered.',
      },
    ],
    useCases: [
      'An administrator enables the billing trigger notification to send every Monday at 08:00 UTC to remind PMs to review pending invoices.',
      'A finance lead enables the Aged WIP Alert so the team is notified when unbilled WIP exceeds the company\'s acceptable threshold.',
      'An administrator disables the Project Health Changed notification to reduce alert noise during a period of planned changes.',
      'A resourcing manager enables the Project Role Unfilled notification with a 24-hour delay to catch projects that are still missing team members the day after creation.',
    ],
  },

  userGuide: {
    gettingStarted:
      'Toggle each notification on or off using the checkbox or switch next to it. For notifications with additional options (such as the billing trigger schedule), extra configuration fields appear when the notification is enabled.',
    steps: [
      {
        title: 'How do I enable or disable a notification?',
        items: [
          { text: 'Find the notification in the list.' },
          { text: 'Click the checkbox or toggle to turn it on or off.' },
          { text: 'Click Save to commit the change.' },
          {
            text: 'The change takes effect immediately after saving.',
            note: 'Notifications are sent to all users with access to the relevant project or feature. Individual users cannot override company-level notification settings from this page.',
          },
        ],
      },
      {
        title: 'How do I configure the billing trigger schedule?',
        items: [
          { text: 'Enable the Billing Trigger Date notification.' },
          { text: 'Select the days of the week you want the notification to be sent by clicking the day buttons (Mon–Sun).' },
          {
            text: 'Set the Send at time field to the UTC time you want the notification dispatched.',
            note: 'The time is in UTC. Adjust for your local timezone — for example, 08:00 UK time in winter is 08:00 UTC, but in summer (BST) it is 07:00 UTC.',
          },
          { text: 'Click Save.' },
        ],
      },
      {
        title: 'How do I set the project role unfilled delay?',
        items: [
          { text: 'Enable the Project Role Unfilled notification.' },
          {
            text: 'Enter the number of hours to wait after project creation before the notification fires.',
            tip: 'A delay of 24–48 hours avoids alert noise for projects that are still being set up.',
          },
          { text: 'Click Save.' },
        ],
      },
    ],
    proTips: [
      'The billing trigger notification is especially useful for T&M businesses — scheduling it on Monday mornings prompts PMs to review timesheets and raise invoices for the prior week.',
      'Combine the Project End Date Approaching and Schedule Overrun notifications to give project managers early warnings before a deadline is missed.',
      'Set the Role Unfilled delay to at least 24 hours — many projects are created with roles to be filled shortly after, so an immediate notification creates noise.',
      'Aged WIP thresholds are configured in WIP Settings, not here. Configure the threshold there first, then enable the notification here.',
    ],
  },

  faq: [
    {
      question: 'Can individual users turn off notifications for themselves?',
      answer:
        'Company-level notification settings on this page apply to all users. Individual users may have personal notification preferences in their user profile, but those cannot override the company-level settings.',
    },
    {
      question: 'What time zone does the billing trigger time use?',
      answer: 'The billing trigger time is in UTC. Account for your local timezone and daylight saving when setting the time.',
    },
    {
      question: 'How do I stop receiving too many notifications?',
      answer:
        'Review each enabled notification and disable any that are generating noise for your team. Consider adding a delay to the Project Role Unfilled notification and reducing the frequency of the billing trigger schedule.',
    },
    {
      question: 'What does the Aged WIP Alert threshold refer to?',
      answer:
        'The threshold value is configured in WIP Settings (not on this page). The notification here simply toggles whether an alert fires when that threshold is crossed.',
    },
    {
      question: 'Why is the billing trigger time sub-panel not showing?',
      answer: 'The billing trigger schedule options only appear when the Billing Trigger Date notification is toggled on.',
    },
  ],

  relatedPages: [
    { label: 'General Settings', path: '/settings/general' },
    { label: 'Project Settings', path: '/settings/project' },
    { label: 'Settings Hub', path: '/settings' },
  ],
};
